import { useIsFocused } from '@react-navigation/native';
import { useStoreState } from '@/stores/store';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, View } from 'react-native';
import {
  download,
  fetchPatientsList,
  getTimeStamps,
  getPermissions,
} from '@/api/services/user';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import ProgressModal from '@/components/SharedComponents/ProgressModal/ProgressModal';
import { useSelectExerciseState } from '@/hooks/useSelectExerciseState';
import {
  createSet,
  hasInternetAsync,
  loadAndClean,
} from '@/screens/HomeTherapist/helpers';
import encryptedStorage from '@/stores/encryptedStorage';
import store from '@/stores/store';
import { hasOwnProperty } from '@/utils/utils';
import { useAssetDownloader } from '@/contexts/DownloadAssetContext';
import { startCheckConsent } from '@/utils/consents';
import { logError } from '@/utils/logHandling';
import { DEVICE_TOKEN_KEY } from '@/utils/secureDeviceToken';
import { Patient, SentenceSet, TextSet, WordSet } from '@/stores/store.types';
import { getCurrentRoute } from '@/navigations/RootNavigation';

const initialContextValue = {
  patients: null as Patient[] | null,
  selectedPatient: null as Patient | null,
  selectedID: '',
  changeID: (
    id: string | null,
    changePatient?: boolean,
    patients?: Patient[],
  ) => {},
  downloadedExercises: false,
  changePatients: (patients: Patient[]) => {},
  changeSelectedPatient: (patient?: Patient) => {},
  fetchPatients: (
    selectedPatient: Patient | null,
    patientsParameter: Patient[] | null,
  ) => Promise.resolve(),
  fetchPatientsWrapper: () => {},
  patientsSyncing: (
    patientsParameter: Patient[] | null,
    stackChanges?: boolean,
  ) => {
    return Promise.resolve();
  },
  showAssetDownloadModal: false,
  setShowStorageWarningModal: (newValue: boolean) => {},
  changeShowAssetDownloadModal: (newValue: boolean) => {},
  mounted: false,
  downloadAssetsOnMobilePlatforms: () => {
    return Promise.resolve();
  },
  toggleRefreshOnCopySets: () => {},
  setAllowefreshOnCopySets: (value: boolean) => {},
};

export const DashboardContext = createContext(initialContextValue);

interface DashboardContextProviderProps {
  children: React.ReactNode;
  id?: string | null;
}
export function DashboardContextProvider({
  children,
  id,
}: DashboardContextProviderProps) {
  const { copySettings } = useSelectExerciseState();
  const [mounted, setMounted] = useState(false);
  const [allowRefreshOnCopySets, setAllowefreshOnCopySets] = useState(false);
  const [refreshOnCopySets, setRefreshOnCopySets] = useState(false);
  const isFocused = useIsFocused();

  const { downloadAssets, hasFreeStorage } = useAssetDownloader();

  const { refresh } = useStoreState((state) => ({
    refresh: state.refresh,
  }));

  const [patients, setPatients] = useState<Patient[] | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(
    patients && patients.length > 0 ? patients[0] : null,
  );
  const [timestamps, setTimestamps] = useState({});
  const [showAssetDownloadModal, setShowAssetDownloadModal] = useState(false);
  const [showStorageWarningModal, setShowStorageWarningModal] = useState(false);
  const [downloadedExercises, setDownloadedExercises] = useState(false);

  const selectedID = selectedPatient?._id ?? '';

  const changeTimestamps = useCallback((timestamps: number) => {
    setTimestamps(timestamps);
  }, []);

  const changeSelectedPatient = useCallback((patient?: Patient) => {
    setSelectedPatient(patient ?? null);
  }, []);

  const changeID = useCallback(
    (id: string | null, changePatient?: boolean, patients?: Patient[]) => {
      if (id && changePatient) {
        const patientData = (patients ?? []).find((p) => p._id === id);
        changeSelectedPatient(patientData);
      } else if (id === null) {
        changeSelectedPatient();
      }
    },
    [changeSelectedPatient],
  );

  const toggleRefreshOnCopySets = useCallback(() => {
    setRefreshOnCopySets((prev) => !prev);
  }, []);

  const changePatients = useCallback((patients: Patient[]) => {
    setPatients(patients);
  }, []);

  const changeShowAssetDownloadModal = useCallback((newValue: boolean) => {
    setShowAssetDownloadModal(newValue);
  }, []);

  const fetchPatients = useCallback(
    async (
      selectedPatient: Patient | null,
      patientsParameter: Patient[] | null,
    ) => {
      try {
        copySettings();
        const patients =
          patientsParameter === null
            ? await fetchPatientsList()
            : patientsParameter;
        changePatients(patients);
        if (patients.length === 0) {
          changeID(null);
          return;
        }
        if (selectedPatient === null) {
          changeSelectedPatient(patients[0]);
        } else {
          const selectedPatientFinding = patients.find(
            (patient: Patient) => patient._id === selectedPatient._id,
          );
          if (selectedPatientFinding) {
            changeSelectedPatient(selectedPatientFinding);
          }
        }
        const payload: {
          wordsetIDs: string[];
          sentencesetIDs: string[];
          textsetIDs: string[];
        } = {
          wordsetIDs: [],
          sentencesetIDs: [],
          textsetIDs: [],
        };

        patients.forEach((eachPatient: Patient) => {
          payload.wordsetIDs = [
            ...payload.wordsetIDs,
            ...eachPatient.activeWordsets,
          ];

          payload.sentencesetIDs = [
            ...payload.sentencesetIDs,
            ...eachPatient.activeSentencesets,
          ];
          payload.textsetIDs = [
            ...payload.textsetIDs,
            ...eachPatient.activeTextsets,
          ];
        });
      } catch (error) {
        logError('Could not fetch patients', error);
      }
    },
    [],
  );

  const fetchPatientsWrapper = useCallback(() => {
    fetchPatients(selectedPatient, null);
  }, [fetchPatients, selectedPatient]);

  const patientsSyncing = async (
    patientsParameter: Patient[] | null,
    stackChanges?: boolean,
  ) => {
    try {
      const patients =
        patientsParameter === null
          ? await fetchPatientsList()
          : patientsParameter;
      await postSyncingPatients(patients, stackChanges);
    } catch (error) {
      logError('Could not sync patients', error);
    }
  };

  const postSyncingPatients = useCallback(
    async (patients: Patient[], stackChanges = false) => {
      setDownloadedExercises(false);
      if (!stackChanges) {
        if (typeof patients === 'undefined') {
          const state = store.getState();
          if (hasOwnProperty(state, 'downloadPatients')) {
            patients = state.downloadPatients ?? [];
          } else {
            patients = [];
          }
        } else {
          store.getActions().setDownloadPatients(patients);
        }
      }

      const setsToKeep: Record<string, boolean> = {};
      const patientIdsToKeep: Record<string, boolean> = {};
      const promises: Promise<unknown>[] = [];

      patients.forEach((patient) => {
        patientIdsToKeep[patient._id] = true;

        patient.wordsets.forEach((wordset) => {
          setsToKeep[wordset._id] = true;
          promises.push(createSet(wordset._id, patient._id, 'word'));
        });

        patient.sentencesets.forEach((sentenceset) => {
          setsToKeep[sentenceset._id] = true;
          promises.push(createSet(sentenceset._id, patient._id, 'sentence'));
        });

        patient.textsets.forEach((textset) => {
          setsToKeep[textset._id] = true;
          promises.push(createSet(textset._id, patient._id, 'text'));
        });
      });

      const [sets, hasEnoughStorage] = await Promise.all([
        Promise.all(promises).then(() =>
          loadAndClean(setsToKeep, patientIdsToKeep),
        ),
        hasFreeStorage(),
      ]);

      await downloadExercises(
        sets as Record<string, WordSet | SentenceSet | TextSet>,
      );

      setDownloadedExercises(true);

      if (hasEnoughStorage) {
        await downloadAssetsOnMobilePlatforms();
      } else {
        setShowStorageWarningModal(true);
      }
    },
    [],
  );

  const downloadAssetsOnMobilePlatforms = async () => {
    try {
      if (
        Platform.OS === 'web' ||
        getCurrentRoute()?.name !== 'PatientProfile'
      ) {
        return;
      }
      let allAssetsLinks: string[] = [];
      let allSetsState: Record<string, WordSet | SentenceSet | TextSet> = {};
      let keys = await encryptedStorage().listItems();
      let allSetsStateArray = keys.filter(
        (k) =>
          k !== '[encryptedStorage][0]' &&
          k !== 'stayLoggedIn' &&
          k !== 'hasSeenNewTemplateModal' &&
          k !== 'minimumAppVersion' &&
          k !== 'seenUpdates' &&
          k !== 'unprotectedDevice' &&
          k !== 'unsupportedIosVersionContinue' &&
          k !== 'unsupportedAndroidVersionContinue' &&
          k !== 'newNavAnimationCountButton' &&
          k !== 'newNavAnimationCountNav' &&
          k !== DEVICE_TOKEN_KEY &&
          !k.includes('sessionBuffer') &&
          !k.includes('starContainer') &&
          !k.includes('__audio__cache'),
      );
      await Promise.all(
        allSetsStateArray.map(async (key) => {
          const value: WordSet | SentenceSet | TextSet =
            (await encryptedStorage().getItem(key)) as
              | WordSet
              | SentenceSet
              | TextSet;
          return (allSetsState[key] = value);
        }),
      );

      for (let key in allSetsState) {
        const localWordset = allSetsState[key];
        const isSync = localWordset.sync;
        if (!isSync) {
          continue;
        }
        const allAssetsFlattened = localWordset.assets?.flat() ?? [];
        allAssetsLinks = [...allAssetsLinks, ...allAssetsFlattened];
      }
      await downloadAssets(allAssetsLinks);

      // testing if it worked
      // const file = await DownloadManager.getLocalUri("https://neolexon-stage-assets.obs-website.eu-de.otc.t-systems.com/neolexon/image/private/t_appimage/production/deutsch/images/827fce01-a865-4c13-bf78-075f1282867d.jpg");
    } catch (error) {
      logError('Could not download assets', error);
    }
  };

  const downloadExercises = useCallback(
    async (wordsets: Record<string, WordSet | SentenceSet | TextSet>) => {
      const promises = [];
      const wordsetIds = [];
      const sentencesetIds = [];
      const textsetIds = [];
      for (let setId in wordsets) {
        const localWordset = wordsets[setId];
        if (
          localWordset.exerciseClass &&
          localWordset.exerciseClass === 'sentence'
        ) {
          sentencesetIds.push(setId);
        } else if (
          localWordset.exerciseClass &&
          localWordset.exerciseClass === 'text'
        ) {
          textsetIds.push(setId);
        } else {
          wordsetIds.push(setId);
        }
      }

      const wordsetIdsObject: {
        wordsetIDs: string[];
        sentencesetIDs: string[];
        textsetIDs: string[];
      } = { wordsetIDs: [], sentencesetIDs: [], textsetIDs: [] };

      wordsetIdsObject.wordsetIDs = wordsetIds;
      wordsetIdsObject.sentencesetIDs = sentencesetIds;
      wordsetIdsObject.textsetIDs = textsetIds;
      const serverTimestamps = await getTimeStamps(wordsetIdsObject);
      changeTimestamps(serverTimestamps);
      for (let setId in wordsets) {
        const localWordset = wordsets[setId];
        let exerciseClass = 'word';
        if (
          localWordset.exerciseClass &&
          localWordset.exerciseClass === 'sentence'
        ) {
          exerciseClass = 'sentence';
        } else if (
          localWordset.exerciseClass &&
          localWordset.exerciseClass === 'text'
        ) {
          exerciseClass = 'text';
        }
        if (localWordset.sync) {
          let url =
            '/api/patients/' +
            localWordset.patientId +
            '/' +
            exerciseClass +
            'sets/' +
            setId +
            '/download';

          promises.push(
            await download(url, setId, localWordset, serverTimestamps),
          );
        }
      }
      return promises;
    },
    [],
  );

  const refreshFunction = async () => {
    try {
      setMounted(true);
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        const objFetchPatientList = await fetchPatientsList(true);
        const [permissionResponse] = await Promise.all([
          getPermissions(true),
          fetchPatients(selectedPatient, objFetchPatientList.patients),
        ]);
        store.getActions().onFirstMountStoreSetup({
          appStartResumeLock: objFetchPatientList.appStartResumeLock,
          eigentrainingLock: objFetchPatientList.eigentrainingLock,
          appLockPattern: objFetchPatientList.appLockPattern,
          patients: objFetchPatientList.patients,
          subscriptions: permissionResponse.subscriptions,
          permissions: permissionResponse.permissions,
        });
        await patientsSyncing(objFetchPatientList.patients, true);
      } else {
        const patients = store.getState().downloadPatients;
        await fetchPatients(selectedPatient, patients);
        await patientsSyncing(patients ?? []);
      }
      await startCheckConsent();
    } catch (error) {
      logError('refreshFunction failed', error);
    }
  };

  useEffect(() => {
    if (id && patients && patients.find((p) => p._id === id)) {
      changeID(id, true, patients);
    }
  }, [id, patients]);

  useEffect(() => {
    if (allowRefreshOnCopySets) {
      refreshFunction();
      setAllowefreshOnCopySets(false);
    }
  }, [refreshOnCopySets, allowRefreshOnCopySets]);

  useEffect(() => {
    // We refresh the permissions when we are on the dashboard only
    if (isFocused) {
      refreshFunction();
    }
  }, [refresh, isFocused]);

  const value = useMemo(() => {
    return {
      patients,
      selectedPatient,
      selectedID,
      downloadedExercises,
      changeID,
      changePatients,
      changeSelectedPatient,
      fetchPatients,
      fetchPatientsWrapper,
      changeTimestamps,
      timestamps,
      patientsSyncing,
      showAssetDownloadModal,
      changeShowAssetDownloadModal,
      setShowStorageWarningModal,
      downloadAssetsOnMobilePlatforms,
      mounted,
      toggleRefreshOnCopySets,
      setAllowefreshOnCopySets,
    };
  }, [
    patients,
    selectedPatient,
    mounted,
    downloadedExercises,
    selectedID,
    changeID,
    changeSelectedPatient,
    fetchPatients,
    fetchPatientsWrapper,
    timestamps,
    showAssetDownloadModal,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      <ProgressModal />
      <View>
        <ConfirmModal
          isVisible={showStorageWarningModal}
          headerText="Zu wenig freier Speicherplatz!"
          customHeight={30}
          contentText="Die neolexon App benötigt mindestens 50MB freien Speicher."
          confirmText="weiter"
          confirmAction={() => setShowStorageWarningModal(false)}
        />
      </View>
      {children}
    </DashboardContext.Provider>
  );
}
